import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { Area, AreaChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';

import styles from './styles.module.scss';

import MetricChartTooltip from './MetricChartTooltip.jsx';
import { MetricChartDotRadius, MetricChartXAxisHeight, MetricChartYAxisWidth } from './constants/index.jsx';
import { MetricChartHeader } from './index.jsx';
import { BreakpointMobile } from '../../constants/ui';
import { ChartColorsMap } from '../Charts/constants/index.jsx';

const ChartTooltip = ({ active, payload, sensor }) => {
  if (!active || !payload || payload.length < 1) return null;
  const value = payload[0].value;
  const valueCompared =
    payload.length > 1
      ? [
          { name: sensor.name, value: `${value}°C`, color: ChartColorsMap.dew },
          {
            name: sensor.name2,
            value: `${payload[1].value}°C`,
            color: ChartColorsMap.dewCompared,
          },
        ]
      : null;
  return value !== null && <MetricChartTooltip value={`${value}°C`} comparedData={valueCompared} />;
};

const MetricChartDew = ({ title, data, sensor, widthViewport }) => {
  const { selectedSecondarySensor } = useSelector((state) => state.sensors);
  const [domainYAsis, setDomainYAsis] = useState(null);
  const [ticksYAsis, setTicksYAsis] = useState(null);

  useEffect(() => {
    const domain = data.domain;
    if (domain && domain.length > 1) {
      setDomainYAsis(domain);
      const ticks = [Math.floor(domain[0]), Math.round(((domain[0] + domain[1]) / 2)), Math.ceil(domain[1])];
      setTicksYAsis(ticks);
    }

    // let values = data.data.map((d) => d.value);
    // if (selectedSecondarySensor) {
    //   const values2 = data.data.map((d) => d.value2);
    //   values = [...values, ...values2];
    // }
    // const min = Math.min(...values);
    // const max = Math.max(...values);
    // const roundedMin = Math.round(min);
    // const roundedMax = Math.round(max);
    // setDomainYAsis([roundedMin, roundedMax]);
    // setTicksYAsis([roundedMin, Number(((roundedMin + roundedMax) / 2).toFixed(1)), roundedMax]);
  }, [data]);

  if (!data) {
    return null;
  }

  return (
    <div className={styles.metricChart}>
      {widthViewport > BreakpointMobile && (
        <MetricChartHeader
          title={title}
          sensorName={sensor.name}
          sensorNameCompared={sensor.name2}
          color={ChartColorsMap.dew}
          colorCompared={ChartColorsMap.dewCompared}
        />
      )}

      {ticksYAsis && (
        <ResponsiveContainer width="100%" height={140} className="chart-report">
          <AreaChart
            width={'100%'}
            height={140}
            data={data.data}
            margin={{
              top: 8,
              right: 8,
              left: 0,
              bottom: 0,
            }}
          >
            <defs>
              <linearGradient id="gradientDew" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={ChartColorsMap.dew} stopOpacity={1} />
                <stop offset="100%" stopColor={ChartColorsMap.dew} stopOpacity={0} />
              </linearGradient>
            </defs>
            <defs>
              <linearGradient id="gradientDewCompared" x1="0" y1="0" x2="0" y2="1">
                <stop stopColor={ChartColorsMap.dewCompared} />
                <stop offset="1" stopOpacity="0" stopColor={ChartColorsMap.dewCompared} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="0 0" stroke={'var(--neutral-8)'} vertical={false} />
            <XAxis dataKey="date" tickLine={false} height={MetricChartXAxisHeight} />
            <YAxis
              domain={domainYAsis}
              ticks={ticksYAsis}
              tickCount={3}
              tickSize={0}
              tickMargin={6}
              width={MetricChartYAxisWidth}
              tickFormatter={(value) => (value > 0 ? `+${value}` : `${value}`)}
            />
            {/*<YAxis ticks={ticksYAsis} tickSize={0} tickMargin={8} width={MetricChartYAxisWidth} domain={domainYAsis} />*/}
            <Tooltip content={<ChartTooltip sensor={sensor} />} />
            <Area
              dataKey="value"
              baseValue="dataMin"
              type="monotone"
              strokeWidth={2}
              stroke={ChartColorsMap.dew}
              fill="url(#gradientDew)"
              fillOpacity={0.2}
              activeDot={{ r: MetricChartDotRadius, fill: '#fff', stroke: ChartColorsMap.dew }}
            />

            {selectedSecondarySensor && data.data[0].value2 !== null && (
              <Area
                dataKey="value2"
                baseValue="dataMin"
                type="monotone"
                strokeWidth={2}
                stroke={ChartColorsMap.dewCompared}
                strokeDasharray="2 2"
                fill="url(#gradientDewCompared)"
                fillOpacity={0.2}
                activeDot={{ r: MetricChartDotRadius, fill: '#fff', stroke: ChartColorsMap.dewCompared }}
              />
            )}
          </AreaChart>
        </ResponsiveContainer>
      )}
      <div className={styles.metricChartUnit}>°C</div>
    </div>
  );
};

export default MetricChartDew;
